import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { sanitizeResponseError } from '@/utils';
import { getCurrentUser, postSignIn, signOutUser } from '../../api/auth';
import type { CurrentUserInterface } from '../../types/User';

const X_SUBDOMAIN_STORAGE_KEY = 'xSubdomain';

function applySignInTenantSubdomain(subdomain: string): void {
  if (!subdomain) {
    return;
  }

  axios.defaults.headers.common['x-subdomain'] = subdomain;
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(X_SUBDOMAIN_STORAGE_KEY, subdomain);
  }
}

function normalizeSignInPayload(payload: any) {
  const source = payload?.data ?? payload ?? {};

  return {
    defaultAccount: source.defaultAccount || source.account || '',
    accountId: source.accountId || 0,
    siteUrl: source.siteUrl || '',
    isSuper: typeof source.isSuper === 'boolean' ? source.isSuper : false,
    onboardingProfile: source.onboardingProfile,
  };
}

interface AuthStateInterface {
  loading: boolean;
  error: boolean;
  errorMsg: string;
  isLoggedIn: boolean;
  currentDomain: string;
  signInAttempted: boolean;
  isLoggedInStatic: boolean;
  data: CurrentUserInterface;
}

const initialState: AuthStateInterface = {
  loading: false,
  error: false,
  errorMsg: '',
  isLoggedIn: false,
  currentDomain: '',
  signInAttempted: false,
  isLoggedInStatic: false,
  data: {
    id: 0,
    name: '',
    email: '',
    role: '',
    title: '',
    type: '',
    profileImageUrl: '',
    account: '',
    accountId: 0,
    roleValue: '',
    privileges: [],
    firstInvite: false,
    createdAt: new Date(),
    active: false,
    siteUrl: '',
    membership: [],
    isSuper: false,
    isTenantSuperAdmin: false,
  },
};

export const tryCurrentUser = createAsyncThunk('auth/current_user', async (_, _arg: any) => {
  const resp = await getCurrentUser();
  return resp.data;
});

export const SignInUser = createAsyncThunk(
  'auth/sign_in',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const signInPayload = await postSignIn(email, password);
      const normalizedPayload = normalizeSignInPayload(signInPayload);
      applySignInTenantSubdomain(normalizedPayload.defaultAccount);
      const currentUserResponse = await getCurrentUser();
      return currentUserResponse.data;
    } catch (error: any) {
      const status = error.response?.status;
      if (status === 422) {
        return rejectWithValue('Invalid params');
      } else if (status === 401) {
        return rejectWithValue(sanitizeResponseError(error));
      } else {
        return rejectWithValue('We could not sign you in at this moment. Please try again later');
      }
    }
  }
);

export const SignOutUser = createAsyncThunk('auth/sign_out', async (_, _arg: any) => {
  const resp = await signOutUser();
  return resp.data;
});

const AuthSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCurrentDomain(state, { payload }) {
      state.currentDomain = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(tryCurrentUser.fulfilled, (state, { payload }) => {
      //Setting Role value for the current user
      state.loading = false;
      const { role } = payload;

      // Log conflict detection: compare isSuper from /current endpoint with previous state
      const previousIsSuper = state.data.isSuper;
      const currentIsSuper = payload.isSuper;
      if (previousIsSuper !== currentIsSuper) {
        console.warn('[Auth Conflict Detection] isSuper mismatch detected', {
          previous: previousIsSuper,
          current: currentIsSuper,
          endpoint: '/current',
        });
      }

      state.data = {
        ...payload,
        roleValue: role,
      };
      state.isLoggedIn = true;

      state.currentDomain = payload.account;
    });
    builder.addCase(tryCurrentUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(tryCurrentUser.rejected, (state, { error }) => {
      state.isLoggedIn = false;
      state.loading = false;
      state.error = true;
      state.errorMsg = `${error.name}:${error.message}`;
    });
    builder.addCase(SignInUser.fulfilled, (state, { payload }) => {
      const { role } = payload;
      state.signInAttempted = true;
      state.loading = false;
      state.isLoggedIn = true;
      state.currentDomain = payload.account;
      state.data = {
        ...payload,
        roleValue: role,
      };
    });
    builder.addCase(SignInUser.pending, (state) => {
      state.signInAttempted = true;
      state.loading = true;
      state.error = false;
      state.errorMsg = '';
    });
    builder.addCase(SignInUser.rejected, (state, { error, payload }) => {
      state.signInAttempted = true;
      state.loading = false;
      state.error = true;
      state.errorMsg = typeof payload === 'string' ? payload : error.message ? error.message : '';
    });
    builder.addCase(SignOutUser.fulfilled, (state) => {
      state.signInAttempted = false;
      state.loading = false;
      state.isLoggedIn = false;
    });
    builder.addCase(SignOutUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(SignOutUser.rejected, (state, { error }) => {
      state.loading = false;
      state.error = true;
      state.errorMsg = error.message ? error.message : '';
    });
  },
});

export const { setCurrentDomain } = AuthSlice.actions;

export default AuthSlice.reducer;
